import CommonModal from "@/components/common/CommonModal";
import I18Translation from "@/components/I18Translation";
import ResetPassword from "@/components/ResetPassword";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import { useUser } from "@/context/useUser";
import { getTenantId, getUserInfo, UserInfo } from "@/lib/storeManager";
import { trimPath } from "@/lib/trimPath";
import { MenuService } from "@/services/APIs";
import type { MenuItem } from "@/services/APIs/MenuService";
import navigationRoutes from "@/utils/navigationRoutes";
import { lightThemeTokens } from "@/utils/themes/lightTheme";
import { IResetPasswordRefType } from "@/utils/types/resetPasswordRef";
import { EyeInvisibleOutlined, LogoutOutlined } from "@ant-design/icons";
import { ErrorBoundary, ProLayout } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Dropdown, Spin } from "antd";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

const CustomHeaderTitle = (_logo: React.ReactNode, _title: React.ReactNode) => {
  return (
    <Link
      to={navigationRoutes.SCHEMAS}
      className="flex items-center gap-2 no-underline"
    >
      <p className="text-lg font-semibold text-foreground">Architecture One</p>
      {/* {logo} */}
      {/* <span className="text-primary text-lg font-bold">{title}</span> */}
      {/* <CaretDownFilled className="text-xs" /> */}
    </Link>
  );
};

const CustomAvatar = memo(function CustomAvatar() {
  const { resetPassword, isAuthenticated, logout } = useUser();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  const [open, setOpen] = useState(false);
  const resetPasswordRef = useRef<IResetPasswordRefType | null>(null);

  useEffect(() => {
    const userInfoData = getUserInfo();
    setUserInfo(userInfoData);
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await logout();
  };

  const handleResetPassword = async () => {
    if (!resetPasswordRef.current) {
      return;
    }

    const values = resetPasswordRef.current.getFieldsValue();
    const response = await resetPassword(values);
    if (response) {
      return response;
    }
  };

  const CustomResetPassword = () => {
    return (
      <CommonModal
        title="Reset Password"
        open={open}
        onOk={() => {
          handleResetPassword(); // ✅ Call handler that uses ref
          setOpen(false);
        }}
        onCancel={() => {
          setOpen(false);
          resetPasswordRef.current?.resetFields(); // ✅ Optional: reset form on close
        }}
        width={800}
        okText="Reset Password"
        cancelText="Cancel"
        confirmLoading={false}
        destroyOnClose
      >
        <ResetPassword ref={resetPasswordRef} />
      </CommonModal>
    );
  };

  return (
    <div className="flex items-center gap-2">
      <Dropdown
        menu={{
          items: [
            {
              key: "theme",
              icon: <ThemeSwitcher />,
            },
            {
              key: "language",
              icon: <I18Translation />,
            },
            {
              key: "resetPassword",
              icon: <EyeInvisibleOutlined />,
              label: "Reset Password",
              onClick: () => setOpen(true),
              disabled: false,
            },
            {
              key: "logout",
              icon: <LogoutOutlined />,
              label: "Logout",
              onClick: handleLogout,
              danger: true,
            },
          ],
        }}
      >
        <div className="flex cursor-pointer items-center gap-2">
          <div className="flex h-8 w-8 select-none items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
            {userInfo?.first_name?.charAt(0)?.toUpperCase() ?? "U"}
          </div>
          <span className="text-sm font-medium">
            {userInfo?.first_name} {userInfo?.last_name}
          </span>
        </div>
      </Dropdown>
      <CustomResetPassword />
    </div>
  );
});

const MainLayout = () => {
  const location = useLocation();
  const tenantId = getTenantId() ?? "";
  const { data: menuData, isLoading } = useQuery({
    queryKey: ["menus"],
    queryFn: () => MenuService.listMenus(),
  });

  const [settings] = useState({
    layout: "mix" as const,
    fixSiderbar: true,
    splitMenus: false,
  });

  const menuItems = useMemo(() => {
    if (!menuData || !Array.isArray(menuData)) {
      return [];
    }
    const map = (items: MenuItem[]): MenuItem[] =>
      items.map(item => ({
        ...item,
        path: item.path,
        name: item.title,
        key: item.id,
        icon: item.icon_url ? (
          <img
            src={item.icon_url}
            alt="icon"
            style={{ width: 20, height: 20 }}
          />
        ) : undefined,
        children: item.children?.length
          ? item.children.map((child: MenuItem) => ({
              ...child,
              path: trimPath(child.path),
              name: child.title,
              key: child.id,
              icon: child.icon_url ? (
                <img
                  src={child.icon_url}
                  alt="icon"
                  style={{ width: 16, height: 16 }}
                />
              ) : undefined,
            }))
          : undefined,
      }));

    return map(menuData);
  }, [menuData]);

  if (isLoading) {
    return <Spin fullscreen />;
  }

  return (
    <ProLayout
      logo={false}
      title={tenantId}
      route={{ path: "/", routes: menuItems ?? [] }}
      location={location}
      navTheme="light"
      fixedHeader
      menuItemRender={(item, dom) => <Link to={item.path!}>{dom}</Link>}
      headerTitleRender={(logo, title) => CustomHeaderTitle(logo, title)}
      token={lightThemeTokens}
      avatarProps={{
        size: "small",
        title: "User",
        render: () => <CustomAvatar />,
      }}
      menuFooterRender={() => (
        <div className="text-center text-sm text-gray-400">
          © {new Date().getFullYear()} Universal Software
        </div>
      )}
      {...settings}
    >
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </ProLayout>
  );
};

export default MainLayout;
